import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BaseComponent, WmsTypeaheadComponent } from "@wms/components";
import { TypeaheadModule } from "ngx-bootstrap/typeahead";
import { FormsModule } from "@angular/forms";
import { DialogService } from '@wms/services';
import { pullAt, map, includes, startsWith, some, set, find, forEach, compact } from "lodash-es";
import { MarkupManagerService, StudioUtils, widgetOptions, WIDGET_CONSTANTS } from "@wms/core";
import { propFactory as PropertiesFactory } from '@wm/components-base';
import { BaseVariableService, VariableCrudManagerService } from '@wms/variables';
import { Subscription } from 'rxjs';
import { CommonWidgetDirective } from "@wms/directives";

@Component({
  selector: 'wms-event-handler',
  standalone: true,
  imports: [CommonModule, FormsModule, TypeaheadModule, WmsTypeaheadComponent, CommonWidgetDirective],
  templateUrl: './event-handler.component.html',
  styleUrls: ['./event-handler.component.less']
})
export class EventHandlerComponent extends BaseComponent implements OnInit {

  @Input() eventName?: string;
  @Input() disable: any;
  @Input() addBtnPosition: string;
  @Input() isVariableDialog: boolean = false;
  @Input() eventOptions: any;
  @Output() onChange: EventEmitter<any> = new EventEmitter();
  @Output() onNavigateAction: EventEmitter<any> = new EventEmitter<any>();
  @Input() typeaheadUid!: string;

  NO_EVENT = WIDGET_CONSTANTS.EVENTS.NO_EVENT;
  EVENT_TYPE = {
    'USER_DEFINED': 'USER_DEFINED',
    'WM_DEFINED': 'WM_DEFINED'
  };

  get eventExpr() {
    return this.expr;
  }

  @Input() set eventExpr(val) {
    this.selected = null;
    this.watch(val);
  }

  index: number = 0;
  expr: string = '';
  fns: any = [];
  _fns: any = [];
  _lastSavedExpr: any;
  selected: any;
  reloadVarSubscription: Subscription;
  onEventOptionsChange: Subscription;
  updateEventsList: Subscription;

  constructor(private dialogService: DialogService, private variableService: BaseVariableService, private variableManagerService: VariableCrudManagerService, private markupManager: MarkupManagerService) {
    super();
    this.reloadVarSubscription = this.variableService.reloadVariablesObs().subscribe((isVarDialog: boolean) => {
      this.dialogService.close('eventsAddVariableModal');
    });
    //need to update event options as login and logout actions are added on enabling security
    this.updateEventsList = this.variableService.updateEventsObs().subscribe(() => {
      this.refreshEvtOptions();
    });
  }
  ngOnInit() {

  }

  ngOnChanges() {
    this.refreshEvtOptions();
  }

  updateEventOptions(newEvent: any) {
    const index = this._fns?.includes("New Action") ? this._fns?.indexOf("New Action") : this._fns?.indexOf("New Variable");
    if (index != -1) {
      newEvent ? this._fns[index] = newEvent : this._fns?.splice(index, 1);
      let val = this._fns.filter(this.isValidEventHandler.bind(this)).join(';');
      if (this._lastSavedExpr !== val) {
        this._lastSavedExpr = val;
        this.onChange.emit(this._lastSavedExpr);
      }
    }
  }

  get widgetEventOptions() {
    return this.eventOptions? this.eventOptions: PropertiesFactory.getWidgetEventOptions();
  }

  set widgetEventOptions(data: Array<any>) {
    PropertiesFactory.setWidgetEventOptions(data);
  }

  refreshEvtOptions() {
    let eventOptions: Array<widgetOptions> = [],
      storewidgetEvtsOptions: any[] = [],
      eventTypes = { 'bottomsheet': [],'dialog': [], 'variable': [], 'prefab': [], 'widgets': [] };

    // find all the dialogs on the page to show the events related to them in properties panel
    
    set(eventTypes, 'dialog', StudioUtils.getDialogOptions(this.markupManager.getMarkup()));

    //variables related events
    set(eventTypes, 'variable', StudioUtils.getVariableOptions());

    //prefab related events
    set(eventTypes, 'prefab', StudioUtils.getPrefabEventOptions(this.markupManager.getMarkup()));

    //Live form and data table related events
    set(eventTypes, 'widgets', StudioUtils.getWidgetMethodOptions(this.markupManager.getMarkup()));

    // add dialog, variable, prefab related events
    eventOptions = eventOptions.concat(eventTypes.dialog, eventTypes.variable, eventTypes.prefab, eventTypes.widgets);

    storewidgetEvtsOptions.length = 0;

    forEach(WIDGET_CONSTANTS.EVENTS_OPTIONS, (opt) => {
      storewidgetEvtsOptions.push({ name: opt, category: 'Default' });
    });

    /* removing all the options except defaults and recreating the options list again,
     * WIDGET_CONSTANTS.EVENTS_OPTIONS.length returns the total length of default options*/
    storewidgetEvtsOptions.length = WIDGET_CONSTANTS.EVENTS_OPTIONS.length;
  
    // assign the modified eventOptions list to the widget
    Array.prototype.push.apply(storewidgetEvtsOptions, compact(eventOptions));
    this.widgetEventOptions = storewidgetEvtsOptions;
  }

  watch(val?: string) {
    this.expr = val || '';
    this.fns = this.expr.split(';')
    this._fns = this.fns.filter((fn: string) => { return !(!fn || fn === this.NO_EVENT); }); //local model with args eg, 'myFunction(arg1, arg2);myFunction2(arg1, arg2)'
    this._lastSavedExpr = this._fns.join(';');
    this.fns = map(this._fns, (fn: string) => {
      let fnType, varName, varObj, fnExpr;
      fnExpr = fn;
      this.index = fn.indexOf('(');
      if (this.index > 0) {
        if (includes(fn, WIDGET_CONSTANTS.EVENTS.STOP_PROP_FN)) {
          fn = WIDGET_CONSTANTS.EVENTS.STOP_PROPAGATION;
        }
        else {
          fn = fn.substring(0, this.index);
        }
      }
      // below code is for variable or Action events
      if (this.isVariableOrActionEvent(fn)) {
        varName = fn.split('.')[1];
        varObj = StudioUtils.getVariableName(varName);
        fn = (varObj && varObj.category === 'wm.WebSocketVariable') ? fn.substring(fn.indexOf('.') + 1) : varName;
      }
      else if (startsWith(fn, 'Widgets.')) {
        fn = fn.substring(fn.indexOf('.') + 1);
      } else {
        fnType = this.getFnType(fnExpr);
      }
      return {
        'fnName': fn,
        'fnType': fnType
      }
    })
    if (!this.fns.length) {
      this.fns.push({ 'fnName': '', 'fnType': '' });
    }
  }

  getFilteredOptions(widgetEventOptions: string[]): string[] {
    return StudioUtils.getFilteredOptions(this.widgetEventOptions);
  }

  getFnType(fnName: string) {
    //If fn is 'Stop Propagation' make it WM_DEFINED as there won't be any script for this in 'Script' tab
    if (fnName !== WIDGET_CONSTANTS.EVENTS.STOP_PROP_FN && !some(this.widgetEventOptions, (opt) => {
      return opt?.name === fnName;
    })) {
      return this.EVENT_TYPE.USER_DEFINED;
    }

    return this.EVENT_TYPE.WM_DEFINED;
  }
  // TODO: move to utils file
  isVariableOrActionEvent(expr: string) {
    return startsWith(expr, 'Variables.') || startsWith(expr, 'Actions.') || startsWith(expr, 'Variables[') || startsWith(expr, 'Actions[');
  }


  addEvtHandler() {
    if (this.fns.length === this._fns.length) {
      this.fns.push([{ 'fnName': '', 'fnType': '' }]);
    }
  }


  isValidEventHandler(fn: any) {
    return !(!fn || fn === this.NO_EVENT);
  }

  _onChange(index?: number, item?: any) {
    let val, newEvent, evtType;

    if (index !== undefined && index > -1 && item) {
      newEvent = item.name;
      evtType = item.type;
      if (index) {
        set(this.fns[index], 'fnName', newEvent);
      }

      if (evtType === 'WIDGET' || evtType === 'PREFAB') {
        this._fns[index] = 'Widgets.' + newEvent + '()';
      } else if (evtType === 'VARIABLE') {
        // this._fns[index] = StudioUtils.getVarExp(newEvent); TODO: getVapExp is a StudioUtils fun
        this._fns[index] = StudioUtils.getVarExp(newEvent);
      } else if (newEvent === WIDGET_CONSTANTS.EVENTS.STOP_PROPAGATION) {
        //If event is Stop Propagation place $event.stopPropagation() in markup
        this._fns[index] = WIDGET_CONSTANTS.EVENTS.STOP_PROP_FN;
      } else if ((newEvent === 'New Action' || newEvent === 'New Variable') && item.category === 'Default' && !this.isVariableDialog) {
        this.variableService.selectedEvt = this.eventName;
        if (newEvent === 'New Variable') {
          this.variableService.currentVariableView = 'Variables';
          this.variableService.initServiceTypesList('data');
        } else {
          this.variableService.currentVariableView = 'Actions';
          this.variableService.initServiceTypesList('action');
        }
        this.variableManagerService.initVariablePropertyList();
        this._fns[index] = newEvent;
        this.dialogService.open('eventsAddVariableModal');
        this.onEventOptionsChange = this.variableService.onEventOptionsChangeObs().subscribe((newEvent: any) => {
          this.updateEventOptions(newEvent);
          this.refreshEvtOptions();
        });
      }
      else {
        this._fns[index] = newEvent;
      }
    }

    val = this._fns.filter(this.isValidEventHandler.bind(this)).join(';');

    if (this._lastSavedExpr !== val) {
      this._lastSavedExpr = val;
      this.onChange.emit(this._lastSavedExpr);
    }

  }

  removeEvtHandler(index: number, clear?: boolean) {

    if (clear) {
      this.fns.splice(index, 1, { 'fnName': '', 'fnType': '' });
      this._fns.splice(index, 1, '');
    } else {
      if (this.fns.length > 1) {
        pullAt(this.fns, index);
      } else {
        this.fns[0] = { 'fnName': '', 'fnType': '' };
      }
      pullAt(this._fns, index);
      this._onChange();
    }
  }

  isExists(fn: any) {
    let optValue;
    if (includes(fn, 'Variables.') || includes(fn, 'Actions.') || includes(fn, 'Widgets.')) {
      optValue = fn.split('.')[1];
      return !find(this.widgetEventOptions, { 'id': optValue });
    }
    //If there is a event other than scriptMethod, Widget, Variable, prefab events then it is invalid event
    if (fn && !includes(fn, '.') && !includes(fn, '(') && fn !== WIDGET_CONSTANTS.EVENTS.JAVASCRIPT && !includes(fn, ' ')) {
      return true;
    }
    return;
  }

  navigateToScript(fnName: string) {
    this.onNavigateAction.emit(fnName);
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.onEventOptionsChange) {
      this.onEventOptionsChange.unsubscribe();
    }
    if (this.updateEventsList) {
      this.updateEventsList.unsubscribe();
    }
  }

}
